/** @jsxImportSource @emotion/react */
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { loadCheckout, prefetchWhenIdle } from './pages/lazyRoutes';
import {
  resolveSentryConfig,
  downgradeNetworkFailures,
  SENTRY_DENY_URLS,
  SENTRY_IGNORE_ERRORS,
  SENTRY_RELEASE,
} from './utils/sentryConfig';

// CSS files
import './index.css';
import './reset.css';
import './fonts/fonts.css';
import './styles/clover-overrides.css';

// Initialize Sentry on every deployed build. We gate on Vite's guaranteed
// `import.meta.env.PROD` (true for any `vite build`, false only on the local
// dev server) rather than a custom env var — the old `VITE_NODE_ENV` gate was
// set in no Netlify context, so Sentry silently never initialized in
// production and all error capture was a no-op. See #476.
const { enabled: sentryEnabled, environment: sentryEnvironment } =
  resolveSentryConfig(import.meta.env);

if (sentryEnabled) {
  Sentry.init({
    dsn: 'https://c3ad941bb92effd6da5b7de93ceba0ac@o4509055999541248.ingest.us.sentry.io/4509119050874880',
    environment: sentryEnvironment,
    release: SENTRY_RELEASE,
    // Known non-actionable noise (#519) — in-app-browser internals injected
    // by wrapper apps (Instagram, etc.), not this site's own code.
    denyUrls: SENTRY_DENY_URLS,
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    // Network errors are not filtered: a Clover or Firebase outage looks like
    // one. They are kept at warning level so one phone dropping its
    // connection never pages anyone (#632).
    beforeSend: downgradeNetworkFailures,
  });
}

// Menu deep-link routes (/liquor) scroll themselves to the menu section on
// load. Leaving scroll restoration on 'auto' lets the browser reapply a saved
// offset from the previous visit part-way through that scroll, landing the
// visitor somewhere arbitrary. See config/menuDeepLinks.js and #514.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

prefetchWhenIdle(loadCheckout);
